// src/encounter/add-to-favourites-dialog.tsx
//
// "Add to favourites" — flow 3 of the encounter header cluster (Andrew's screenshot, 2026-08-08).
//
// A CENTRE MODAL, not a drawer — the one surface of the three that is not the left drawer. It is
// the narrowest of the three too: the tool strip across the top, a search field with the ICD-10
// link on its right, and results. No favourites block, no Global block, no Suggest — you are here
// to CHOOSE what becomes a favourite, so showing the existing favourites would be circular.
//
// That is why this composes `useApCategorySearch` directly rather than `ApSearchRail`: the rail's
// whole anatomy is favourites-first + auto-widen to Global, which is exactly what this surface must
// not do. Only the tool strip is shared in appearance, and it is 7 buttons.
//
// Empty state is the live app's literal copy: "No item found. Try search with different query".
//
// Add AND remove: the ☆ toggles. Both are the same POST with `isDeleted` (see use-favourites.ts),
// and the affordance is hidden entirely for categories with no favourites route (referral,
// education) rather than offering a control that would 404.
import { useState } from 'react';
import {
  type ApSearchCategory,
  type ApSearchItem,
} from '@scripsteam/scrips-signal-ds';
import { ClipboardList, FlaskConical, Pill, ScanLine, Send, Syringe, type LucideIcon } from 'lucide-react';
import { useApCategorySearch } from './services/use-ap-category-search';
import { useFavourite, canFavourite } from './services/use-favourites';
import { useDesignV2 } from './lib/design-v2';

// The category marks are the order-set type map's Lucide glyphs (icon pass, 16 Oct; were the Scrips font).
// Diagnosis keeps its "Dx" text mark.
const STRIP_META: { category: ApSearchCategory; glyph: LucideIcon | null; label: string }[] = [
  { category: 'diagnosis', glyph: null, label: 'Diagnosis' },
  { category: 'lab', glyph: FlaskConical, label: 'Labs' },
  { category: 'medication', glyph: Pill, label: 'Medications' },
  { category: 'vaccination', glyph: Syringe, label: 'Vaccinations' },
  { category: 'imaging', glyph: ScanLine, label: 'Radiology' },
  { category: 'procedure', glyph: ClipboardList, label: 'Procedures' },
  { category: 'referral', glyph: Send, label: 'Referrals' },
];

// 32 x 30 = spacing steps 8 x 7.5.
const SEGMENT = 'flex items-center justify-center w-8 h-control-pill p-0 border-0 cursor-pointer';

export interface AddToFavouritesDialogProps {
  open: boolean;
  onClose: () => void;
  /** The provider these favourites belong to. */
  providerId?: string;
  /** Tabs to offer — the same permission DATA the rail takes. */
  categories: ApSearchCategory[];
}

/**
 * The modal is a THIN GATE over its body on purpose. Every hook below (the category searches, the
 * favourite mutation) lives in `AddToFavouritesDialogBody`, so a closed dialog runs none of them —
 * an early `return null` AFTER the hooks would still fire a search on every encounter render, and
 * would demand a QueryClient from any host that never opens this surface.
 */
export function AddToFavouritesDialog({ open, ...rest }: AddToFavouritesDialogProps) {
  if (!open) return null;
  return <AddToFavouritesDialogBody {...rest} />;
}

function AddToFavouritesDialogBody({
  onClose,
  providerId,
  categories,
}: Omit<AddToFavouritesDialogProps, 'open'>) {
  const [active, setActive] = useState<ApSearchCategory>(categories[0] ?? 'diagnosis');
  // Design sheet v1.1, switch ON only: the code-system caption is a label, not an action.
  const designV2 = useDesignV2();
  const [query, setQuery] = useState('');
  const favourite = useFavourite();
  const search = useApCategorySearch(providerId, active, query, true);

  // Favourites-vs-global is the rail's concern; here every hit is a candidate.
  const rows: ApSearchItem[] = [
    ...(search.items.favourites ?? []),
    ...(search.items.results ?? []),
  ];
  const strip = STRIP_META.filter((m) => categories.includes(m.category));

  return (
    <div
      role="dialog"
      aria-modal="true"
      aria-label="Add to favourites"
      data-testid="add-to-favourites-dialog"
      className="fixed inset-0 z-50 flex items-center justify-center bg-surface-overlay/25 font-sans"
      onClick={onClose}
    >
      <div
        onClick={(e) => e.stopPropagation()}
        /* FIXED HEIGHT, not max-height (Andrew, 2026-08-10). Every tab in this popup returns a
           different number of rows, and the empty / "Searching…" states are a single line — so a
           max-height box resized on every tool-strip switch and every keystroke, jumping the
           dialog around its own centre. A fixed height makes the popup independent of what the
           active tab found; only the results area inside it scrolls. `h-140` (560) is what keeps the
           popup the same size on every tab; `w-favourites-popup` = min(560px, 92vw) and
           `max-h-favourites-popup` = 78vh keep it inside a small viewport. */
        className="w-favourites-popup h-140 max-h-favourites-popup flex flex-col bg-surface-card rounded-lg shadow-8 overflow-hidden"
      >
        {/* Header: title, tool strip, ✕ — all on one row, as drawn. */}
        <div className="flex items-center gap-3 py-3.5 px-4">
          <span className="text-size-sm font-semibold text-text-secondary">
            Add to favourites
          </span>
          <div className="flex border border-solid border-border-default rounded-md overflow-hidden">
            {strip.map((m) => {
              const on = m.category === active;
              return (
                <button
                  key={m.category}
                  type="button"
                  role="radio"
                  aria-checked={on}
                  aria-label={m.label}
                  onClick={() => {
                    setActive(m.category);
                    setQuery('');
                  }}
                  className={`${SEGMENT} ${on ? 'bg-brand-primary text-text-inverse' : 'bg-surface-card text-text-muted'}`}
                >
                  {m.glyph ? (
                    <m.glyph className="size-4" aria-hidden />
                  ) : (
                    <span className="text-size-eyebrow font-bold">Dx</span>
                  )}
                </button>
              );
            })}
          </div>
          <span className="flex-1" />
          <button
            type="button"
            onClick={onClose}
            aria-label="Close add to favourites"
            className="border-0 bg-transparent text-text-muted text-size-base cursor-pointer p-0"
          >
            ✕
          </button>
        </div>

        <div className="flex items-center gap-3 pt-0 px-4 pb-3">
          <input
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            placeholder="Search"
            aria-label={`Search ${active}`}
            className="flex-1 min-w-0 h-8 py-0 px-3 text-size-eyebrow font-sans text-text-primary bg-surface-card border border-solid border-border-default rounded-md"
          />
          {active === 'diagnosis' && (
            <span className={designV2 ? 'text-size-xs font-bold text-text-secondary' : 'text-size-xs font-bold text-brand-primary'}>
              ICD-10
            </span>
          )}
        </div>

        <div className="flex-1 min-h-0 overflow-y-auto pt-0 px-4 pb-4">
          {search.loading ? (
            <p className="text-size-eyebrow text-text-hint" role="status" aria-live="polite">
              Searching…
            </p>
          ) : rows.length === 0 ? (
            <p
              className="text-size-eyebrow text-text-hint text-center py-12 px-0"
              role="status"
              aria-live="polite"
            >
              No item found. Try search with different query
            </p>
          ) : (
            <ul className="list-none m-0 p-0">
              {rows.map((row, i) => (
                <li
                  key={row.id ?? `${row.label}-${i}`}
                  className="flex items-center gap-2 py-1.5 px-0 text-size-sm text-text-primary"
                >
                  {row.code && (
                    <span className="w-16 shrink-0 text-size-xs font-mono text-text-muted">
                      {row.code}
                    </span>
                  )}
                  <span className="flex-1 min-w-0">{row.label}</span>
                  {providerId && canFavourite(active) && (
                    <button
                      type="button"
                      onClick={() =>
                        favourite.mutate({
                          category: active,
                          providerId,
                          item: (row.item ?? row) as Record<string, unknown>,
                        })
                      }
                      disabled={favourite.isPending}
                      aria-label={`Add ${row.label} to favourites`}
                      className={`border-0 bg-transparent text-brand-primary text-size-base leading-none ${favourite.isPending ? 'cursor-not-allowed opacity-50' : 'cursor-pointer opacity-100'}`}
                    >
                      ☆
                    </button>
                  )}
                </li>
              ))}
            </ul>
          )}
        </div>
      </div>
    </div>
  );
}
